import {
  type AnnouncementItem,
  addDays,
  minutesSince,
  timeOf,
  todayIso,
  useActiveAlarm,
  useAlarmActions,
  useAnnouncementActions,
  useAnnouncements,
  useAuth,
  useChildAlarmStatus,
} from '@shkola/api';
import { tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpenTextIcon,
  CaretRightIcon,
  MegaphoneIcon,
  SirenIcon,
  WindIcon,
} from '../../design/icons';
import { buttonClass } from '../../design/ui';
import { readEnv } from '../../env';
import { useMinute } from '../../lib/now';
import { AbroadCard } from '../AbroadPage';
import { type StudentContext, dayMonthShort } from './model';

/*
 * The blocks of the moment of «Скло 2» (his words 27.10: «ситуативні блоки — є, коли щось сталося, і
 * зникають, коли ситуація скінчилась; з’являються й зникають, нічого не ламаючи»). On top of the home,
 * before everything, in the order of urgency: an alarm (air or a drill), the announcements the student
 * has to confirm, the student abroad. None — nothing at all, not even a gap. The other blocks of the home
 * come and go the same way (Glass2Home): the new grades, the school, the cards of what to do.
 * On the preview `?sim=alarm|drill|notice|nogrades|onegrade|notasks|onetask|noschool` (comma-separated)
 * shows a situation without making it happen: an alarm on the demo would reach every phone.
 */

const PREVIEW =
  readEnv(import.meta.env as Record<string, string | undefined>).appEnv !== 'production';

export type Sim =
  | 'alarm'
  | 'drill'
  | 'notice'
  | 'nogrades'
  | 'onegrade'
  | 'notasks'
  | 'onetask'
  | 'noschool'
  /** «Форми 2»: the sheet of all its pictures instead of the home */
  | 'art';

/** The situations asked for on the preview (none in production). */
export function useSim(): ReadonlySet<Sim> {
  if (!PREVIEW || typeof window === 'undefined') return new Set();
  const asked = new URLSearchParams(window.location.search).get('sim') ?? '';
  return new Set(asked.split(',').filter(Boolean) as Sim[]);
}

export type AlarmLike = Pick<
  NonNullable<ReturnType<typeof useActiveAlarm>['data']>,
  'id' | 'kind' | 'started_at'
>;

/** How the student was marked in the roll-call of the alarm. */
export function Marked({ ctx, sessionId }: { ctx: StudentContext; sessionId: string }) {
  const { t } = useTranslation();
  const status = useChildAlarmStatus(sessionId, ctx.child.memberId);
  if (!status.data) return null;
  return (
    <>
      {' · '}
      {t('alarm.child.marked', {
        name: ctx.child.firstName,
        status: t(`alarm.status.${status.data.status}`),
        time: timeOf(status.data.marked_at),
      })}
    </>
  );
}

/** An alarm across the whole top: what it is and how long, the roll-call, what to do now. */
function AlarmStrip({
  ctx,
  session,
  preview,
}: {
  ctx: StudentContext;
  session: AlarmLike;
  preview: boolean;
}) {
  const { t } = useTranslation();
  const actions = useAlarmActions();
  useMinute();
  const drill = session.kind === 'drill';
  const kind = t(`alarm.kinds.${session.kind}`);
  // an air alarm in red with white words; a drill in the soft yellow, navy words
  const ground = drill
    ? `linear-gradient(110deg, ${tones.yellow.from} 0%, ${tones.yellow.to} 100%)`
    : 'linear-gradient(110deg, #E5484D 0%, #EF5A5E 55%, #F7785F 100%)';
  const ink = drill ? 'text-ink' : 'text-white';
  const chip = `inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition-colors ${
    drill ? 'bg-white text-ink hover:bg-white/80' : 'bg-white/20 text-white hover:bg-white/30'
  }`;
  return (
    <div
      role="alert"
      className={`flex min-w-0 flex-col gap-4 rounded-[32px] p-4 md:p-5 lg:flex-row lg:items-center ${ink}`}
      style={{ backgroundImage: ground }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span
          className="relative flex size-12 shrink-0 items-center justify-center rounded-full bg-white"
          style={{ color: drill ? tones.yellow.vivid : '#E5484D' }}
        >
          <span className="absolute inset-0 animate-ping rounded-full bg-white/60" />
          <SirenIcon size={24} weight="fill" aria-hidden className="relative" />
        </span>
        <div className="min-w-0">
          <p className="font-display text-[18px] leading-6 font-semibold md:text-[20px] md:leading-7">
            {t('alarm.banner.student', { kind })}
          </p>
          <p className={`text-[14px] leading-5 ${drill ? 'text-ink-soft' : 'text-white/85'}`}>
            {t('alarm.duration', { minutes: minutesSince(session.started_at) })}
            {!preview && <Marked ctx={ctx} sessionId={session.id} />}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={preview}
          className={chip}
          onClick={() =>
            actions.mark.mutate({
              schoolId: ctx.schoolId,
              sessionId: session.id,
              marks: [{ studentId: ctx.child.memberId, status: 'safe_elsewhere' }],
            })
          }
        >
          {t('alarm.imSafe')}
        </button>
        <Link to="/care" search={{ open: 'calm' }} className={chip}>
          <WindIcon size={17} weight="fill" aria-hidden />
          {t('care.calm.short')}
        </Link>
        <Link to="/alarm" className={chip}>
          <BookOpenTextIcon size={17} weight="fill" aria-hidden />
          {t('shelter.open')}
        </Link>
        <Link
          to="/alarm"
          className={`inline-flex h-10 shrink-0 items-center rounded-full px-5 text-[14px] font-semibold ${
            drill ? 'bg-primary text-white' : 'bg-white text-[#C8373C]'
          }`}
        >
          {t('alarm.banner.open')}
        </Link>
      </div>
    </div>
  );
}

/** An announcement the student has to confirm: the first in full, the others counted. */
function NoticeStrip({
  items,
  onAck,
}: {
  items: ReadonlyArray<Notice>;
  onAck: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [first, ...others] = items;
  if (!first) return null;
  const tint = tones.orange;
  return (
    <div
      className="flex min-w-0 flex-col gap-3 rounded-[32px] p-4 sm:flex-row sm:items-center md:p-5"
      style={{ backgroundImage: `linear-gradient(110deg, ${tint.from} 0%, ${tint.to} 100%)` }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white"
          style={{ color: tint.vivid }}
        >
          <MegaphoneIcon size={22} weight="fill" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-2">
            <span
              className="shrink-0 rounded-full bg-white px-2.5 py-0.5 text-[12px] font-semibold"
              style={{ color: tint.vivid }}
            >
              {t('studentHome.glass2.notice.label')}
            </span>
            <span className="truncate font-display text-[17px] leading-6 font-semibold text-ink">
              {first.title}
            </span>
          </p>
          <p className="mt-0.5 truncate text-[13px] leading-5" style={{ color: tint.ink }}>
            {[first.author, dayMonthShort(first.publishedAt.slice(0, 10))]
              .filter(Boolean)
              .join(' · ')}
            {others.length > 0 && (
              <Link to="/announcements" className="ml-2 font-semibold hover:underline">
                {t('studentHome.glass2.notice.more', { count: others.length })}
                <CaretRightIcon size={11} weight="bold" aria-hidden className="ml-0.5 inline" />
              </Link>
            )}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Link
          to="/announcements"
          className="inline-flex h-10 items-center rounded-full bg-white px-4 text-[14px] font-semibold text-ink transition-colors hover:bg-white/80"
        >
          {t('studentHome.glass2.notice.open')}
        </Link>
        <button
          type="button"
          onClick={() => onAck(first.id)}
          className={buttonClass('primary', 'md')}
        >
          {t('studentHome.glass2.notice.ack')}
        </button>
      </div>
    </div>
  );
}

/**
 * What is going on at the moment (the data of the blocks of the moment, apart from how a look draws
 * them): the alarm open in the school, the announcements the student has to confirm and how to confirm
 * one; on the preview the situations asked for with `?sim=`.
 */
export function useMoment(ctx: StudentContext) {
  const { t } = useTranslation();
  const sim = useSim();
  const { me } = useAuth();
  const alarm = useActiveAlarm(ctx.schoolId);
  const memberIds = (me?.memberships ?? [])
    .filter((m) => m.school_id === ctx.schoolId)
    .map((m) => m.member_id);
  const announcements = useAnnouncements(ctx.schoolId, memberIds);
  const { markRead } = useAnnouncementActions();
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());
  const today = todayIso();

  const preview = sim.has('alarm') || sim.has('drill');
  const session: AlarmLike | null | undefined = preview
    ? {
        id: 'preview',
        kind: sim.has('drill') ? 'drill' : 'air',
        started_at: new Date(Date.now() - 7 * 60_000).toISOString(),
      }
    : alarm.data;

  const notices: Notice[] = [
    ...(sim.has('notice')
      ? [
          {
            id: 'preview',
            title: t('studentHome.glass2.sim.noticeTitle'),
            author: t('studentHome.glass2.sim.noticeAuthor'),
            publishedAt: new Date().toISOString(),
          },
        ]
      : []),
    ...(announcements.data ?? []).filter(
      (a) =>
        a.isRecipient &&
        a.requiresAck &&
        !a.readAt &&
        a.publishedAt.slice(0, 10) >= addDays(today, -14),
    ),
  ].filter((a) => !acked.has(a.id));

  const ack = (id: string) => {
    setAcked((s) => new Set(s).add(id));
    if (id !== 'preview') markRead.mutate({ announcementId: id, memberIds });
  };

  return { session, preview, notices, ack };
}

export type Notice = Pick<AnnouncementItem, 'id' | 'title' | 'author' | 'publishedAt'>;

/** The blocks of the moment, most urgent first; nothing when nothing is going on. */
export function Situations({ ctx, className = '' }: { ctx: StudentContext; className?: string }) {
  const { session, preview, notices, ack } = useMoment(ctx);
  return (
    <div className={`flex min-w-0 flex-col gap-2 empty:hidden ${className}`}>
      {session && <AlarmStrip ctx={ctx} session={session} preview={preview} />}
      {notices.length > 0 && <NoticeStrip items={notices} onAck={ack} />}
      <AbroadCard studentId={ctx.child.memberId} self />
    </div>
  );
}
